/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {ValidatorFn, Validators} from '@angular/forms';
import {TooltipEnum} from '../../../../shared/tooltip/tooltip.enum';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {take} from 'rxjs/operators';
import {timer} from 'rxjs';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {InputListModel} from '../input-list.model';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {ReasonModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/reason.model';

@Component({
    selector: 'app-reason-modal',
    templateUrl: './reason-modal.component.html',
    styleUrls: ['../business-settings-modal.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReasonModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<ReasonModel>> {

    @ViewChild('reasonName') nameInput: InputTextComponent;
    @ViewChild('reasonCode') codeInput: InputTextComponent;

    readonly EDIT_TITLE = 'Edit reason';
    readonly ADD_TITLE = 'Add a reason';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';
    readonly ENTITY_NAME = 'reason';

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        code: [Validators.pattern(/^\w{0,3}$/)],
    };

    TooltipEnum = TooltipEnum;

    public nameMessages = new Map<WarningsEnum, string[]>();
    public codeMessages = new Map<WarningsEnum, string[]>();

    constructor(public typeahead: TypeaheadHelperService, inputMessages: InputMessagesService) {
        super(inputMessages);
    }

    setEntity(element: EntityElementModel<ReasonModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: element.currentValue
        });
        this.onReady.pipe(take(1))
.subscribe(() => {
            timer(0)
.subscribe(() => {
                this.nameInput.setValue(this.state.currentValue.name);
                this.codeInput.setValue(this.state.currentValue.code);
                this.setActiveInputState(element.active, true);
            });
        });
    }

    nameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.nameInput.inputControl, this.nameMessages, this.nameInput.label);
        this.nameMessages = new Map(this.nameMessages);
        const errors = this.nameMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });
        this.setModalValidity();
    }

    codeInputChange(code: string): void {
        const codeInUpperCase = code != null ? code.toUpperCase() : null;

        this.inputMessages.mandatoryWarnings(this.codeInput.inputControl, this.codeMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.codeInput.inputControl,
                this.codeMessages,
                this.codeInput.label,
                'AX3'
        );

        this.codeMessages = new Map(this.codeMessages);

        const errors = this.codeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(codeInUpperCase, this.ENTITY_NAME, this.codeMessages, this.codeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: codeInUpperCase
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });
        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }];
    }
}
